import { Component, ReactNode } from 'react';
import { TextField, TextFieldProps, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            minHeight: '32px',
            height: '100%',

            '& > div': {
                height: '100%',
            },
        },
        readOnly: {
            border: 'none',
            borderRadius: '4px',
            '& > div, textarea': {
                backgroundColor: theme.palette.background.default,
                opacity: 0.6,
                color: '#21272E',
            },
            '& > label': {
                color: '#21272E',
                opacity: 0.6,
                '&.Mui-focused': {
                    color: '#21272E',
                },
            },
            '& > div': {
                '&:hover': {
                    '& > fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                },
                '&.Mui-focused > fieldset.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: 1,
                },
            },
        },
        small: {
            '& > label': {
                display: 'none',
            },
            '& > div > div': {
                marginBottom: 4, // border radius
            },
            '& > div textarea': {
                marginTop: 0,
                padding: '2px 0em 2px 0.9em',
                fontSize: '12px',
                overflow: 'auto',
                width: 'calc(100% - 0.9em);',
            },
        },
        withRows: {
            height: 'auto',
        },
    });

interface PoolTextAreaBaseProps extends WithStyles<typeof styles> {
    readOnly?: boolean;
    fieldType?: keyof ReturnType<typeof styles>;
    rowsMax?: number | string;
    hint?: ReactNode;
}

export type PoolTextAreaProps = PoolTextAreaBaseProps & TextFieldProps;

export const PoolTextArea = withStyles(styles)(
    class PoolTextAreaBase extends Component<PoolTextAreaProps> {
        private static getDisplayClass(props: PoolTextAreaProps): string {
            if (props.fieldType) {
                return props.classes[props.fieldType];
            }

            return '';
        }

        private static getClassNames(props: PoolTextAreaProps) {
            return `${props.classes.container} ${this.getDisplayClass(props)} ${
                props.readOnly ? props.classes.readOnly : ''
            } ${props.rows ? props.classes.withRows : ''}`;
        }

        public override render() {
            const {
                classes: _classes,
                fieldType: _fieldType,
                rowsMax,
                maxRows,
                readOnly,
                hint,
                ...baseTextareaProps
            } = this.props;
            const mRows = maxRows || rowsMax;

            const helperText = this.props.helperText && this.props.error ? this.props.helperText : hint || '';

            return (
                <TextField
                    {...baseTextareaProps}
                    inputProps={{
                        readOnly,
                        tabIndex: readOnly || this.props.disabled ? -1 : undefined,
                    }}
                    variant="outlined"
                    className={PoolTextAreaBase.getClassNames(this.props)}
                    multiline={true}
                    disabled={this.props.disabled || (readOnly && !this.props.value)}
                    rows={mRows ? undefined : this.props.rows}
                    minRows={mRows ? this.props.rows : undefined}
                    maxRows={mRows}
                    helperText={helperText}
                />
            );
        }
    }
);
